import React from "react"
import { Link, useLocation } from 'react-router-dom'
import bulbIcon from '../../../../assets/images/icons/bulb.png'
import { buildListsendDashboardUrl } from '../../utils/listsendAuth'
import { navigateToPressReleaseApp } from '../../utils/pressReleaseAuth'

const ChannelCard = ({boostingEventId, eventId, channel, isBoosted, companySlug, upsellAvailable, isPublished, isInPast, hasAccess = true, pressReleaseV2 = false}) => {
  const location = useLocation();
  const search = location.search;
  const isDisabled = isInPast || isBoosted || channel?.isComingSoon;
  const isEventCalendars = channel.slug === 'event_calendars';
  const isMetaAdsAI = channel.slug === 'meta_ads_ai';
  const isGoogleAdsAI = channel.slug === 'google_ads_ai';
  const isSocialMediaAI = channel.slug === 'social_media_ai';
  const isEmailMarketingAI = channel.slug === 'email_marketing_ai';
  // Press Release v2 gate ON: the card hands off to the external Press Release app exactly the
  // way the email card hands off to ListSend (same token scheme). Gate OFF = legacy in-app flow.
  const isPressReleaseApp = channel.slug === 'press_release_ai' && pressReleaseV2;
  const isExternalHandoff = isEmailMarketingAI || isPressReleaseApp;

  // Get Facebook connection status from DOM (set by Rails controller)
  const getFacebookStatusFromDOM = () => {
    const reactRoot = document.getElementById('react-root');
    const facebookConnected = reactRoot?.getAttribute('facebook-connected') === 'true';
    const noAdAccounts = reactRoot?.getAttribute('no-ad-accounts') === 'true';
    return { connected: facebookConnected, noAdAccounts: noAdAccounts };
  };

  const facebookData = getFacebookStatusFromDOM();

  const openPopup = (url) => {
    const width = 600;
    const height = 700;
    const popupLeft = (window.innerWidth - width) / 2;
    const popupTop = (window.innerHeight - height) / 2;

    const popup = window.open(
      url,
      `fbAuth_${Date.now()}`,
      `popup=yes,width=${width},height=${height},top=${popupTop},left=${popupLeft},resizable=yes,scrollbars=yes,toolbar=0,location=0,menubar=0,status=0`
    );

    if (!popup) {
      alert('Please allow popups in your browser. Redirecting...');
      window.location.href = url;
    }
  };

  // If no subscription access, link to the product page instead of the tool
  const productPageHref = `/marketing/channels/${channel.slug}`;

  // For SocialMediaAI, MetaAdsAI, and GoogleAdsAI, always use the direct path to ensure server-side handling
  const href = !hasAccess
    ? productPageHref
    : eventId
      ? ((isDisabled || !upsellAvailable) ? (search || '#') : `/events/${eventId}/promote`)
      : ((isDisabled || !upsellAvailable) && !(isMetaAdsAI || isSocialMediaAI || isGoogleAdsAI))
        ? (search || '#')
        : boostingEventId
          ? `/marketing/${channel.slug}?event_id=${boostingEventId}`
          : `/marketing/${channel.slug}`;

  const handleCardClick = (e) => {
    // If no subscription access, navigate to product page (don't block)
    if (!hasAccess) return;

    if (isPressReleaseApp) {
      return navigateToPressReleaseApp(e);
    }

    if (isEmailMarketingAI) {
      e.preventDefault();
      e.stopPropagation();

      const redirectUrl = buildListsendDashboardUrl();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        console.error('Failed to build listsend URL (missing company DOM attrs)');
        alert('Company information not found. Please refresh the page and try again.');
      }
      return false;
    }

    // For SocialMediaAI, always redirect to the page (modal will auto-open if not connected)
    if (isSocialMediaAI && !channel?.isComingSoon) {
      // Let the link proceed to the social media page
      return;
    }

    // For MetaAdsAI, always redirect to the page (modal will auto-open if not connected)
    if (isMetaAdsAI && !channel?.isComingSoon) {
      return;
    }

    // For GoogleAdsAI, always redirect to the page
    if (isGoogleAdsAI && !channel?.isComingSoon) {
      return;
    }

    // For other cases, keep the existing behavior
    if (isDisabled || !upsellAvailable) {
      e.preventDefault();
    }
  };

  // For channels without access, use Link to product page; otherwise use 'a' tag for server-side handling where needed
  const Element = !hasAccess
    ? Link
    : (eventId || isMetaAdsAI || isGoogleAdsAI || isSocialMediaAI || isExternalHandoff) ? 'a' : Link;
  const elementProps = !hasAccess
    ? { to: productPageHref }
    : (eventId || isMetaAdsAI || isGoogleAdsAI || isSocialMediaAI || isExternalHandoff)
      ? { href: isExternalHandoff ? '#' : href }
      : { to: href };

  const getUpsellMessage = () => {
    if (isDisabled) {

      if (channel.isComingSoon) {
        return channel.additionalText;
      }
      if (!isPublished) {
        return "This event hasn't been published yet.";
      } else if (isInPast) {
        return "This event has already ended.";
      } else {
        return channel.additionalText;
      }
    }
    return "You've already promoted this event through this channel.";
  };

  if (!eventId && isEventCalendars && !isDisabled) {
    elementProps.target = '_blank';
    elementProps.rel = 'noopener noreferrer';
  }

  return (
    <>
      <Element
        {...elementProps}
        onClick={handleCardClick}
        className={`marketing__content__card ${isDisabled && !isExternalHandoff ? 'no-pointer' : ''}`}
        style={{ background: channel.gradient, cursor: isExternalHandoff ? 'pointer' : undefined }}
      >
      {hasAccess && (isDisabled || !upsellAvailable) && !isExternalHandoff && (
        <div className={`popover-text ${!channel.isComingSoon ? 'popover-text-plain' : ''}`} style={{ background: channel.additionalGradient }}>
          {getUpsellMessage()}
        </div>
      )}
      <div>
        <h2>{channel.title}</h2>
        <h6>{channel.subTitle}</h6>
      </div>
      <p>{channel.description}</p>
      <div>
        <h6>
          <img src={bulbIcon} alt="bulb Icon" width="23" height="23" />
          Best For:
        </h6>
        <p>{channel.bestFor}</p>
      </div>
    </Element>
    </>
  );
}

export default ChannelCard